/**
 * Le planning d'une contre-étude, dans sa propre fenêtre.
 *
 * *Demande de Chris, 22/09/2026 : « je voudrais pouvoir ouvrir le planning dans une fenêtre
 * différente que je pourrais mettre sur un deuxième écran et en même temps consulter la
 * contre-étude ».*
 *
 * ## Ni menu, ni titre d'application
 *
 * Même parti que la fenêtre du plan de charge (`PlanningRessources`, 03/09/2026) : la place sert
 * à la frise, et un menu inviterait à naviguer dans une fenêtre qui n'existe que pour être
 * regardée à côté d'une autre. Ce qui reste en tête, c'est **de quoi on regarde le planning** —
 * un deuxième écran se consulte de loin, et deux fenêtres qui se ressemblent sur deux affaires
 * différentes sont le meilleur moyen de modifier la mauvaise.
 *
 * ## Elle relit, elle ne reçoit pas
 *
 * Quand la contre-étude change dans l'autre fenêtre, celle-ci **relit la version au serveur**.
 * Elle ne reçoit aucun chiffre par le canal : voir `synchroVersion.ts`. Et dans l'autre sens,
 * tout ce qui se pose ici sur le planning peut changer le déboursé — c'est déjà la raison du
 * `onChange` de `Planning` — donc chaque modification est signalée à l'autre fenêtre.
 *
 * ## Son propre chargement, et pas celui de l'application
 *
 * `Application` charge au montage les chantiers, les génériques et les regroupements : le
 * planning n'en a besoin d'aucun. Cette fenêtre lit **la version et les carrières**, ce que
 * `Planning` demande, et rien de plus. C'est une fenêtre qui s'ouvre sur un deuxième écran
 * pendant qu'on travaille dans la première : ce qu'elle ne demande pas au serveur est autant
 * qu'elle ne lui prend pas.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import type { ReponseComparaison } from "@planif/moteur";
import { chargerCarrieres, chargerVersion, etatAssistant } from "./api";
import { adresseDeLAssistant, nomFenetreAssistant } from "./adresseAssistant";
import type { Carriere } from "./api";
import { Planning } from "./Planning";
import { versionDeLAdresse } from "./fenetreDuPlanning";
import { ecouterVersion, signalerVersionModifiee } from "./synchroVersion";

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; reponse: ReponseComparaison }
  | { phase: "erreur"; raison: string };

export function FenetrePlanning(): JSX.Element {
  /*
    Lue **une fois au montage**, comme la fenêtre du plan de charge lit la sienne : l'adresse ne
    change pas sous les pieds de la fenêtre, et la relire à chaque rendu ferait dépendre l'écran
    d'une valeur qui n'est pas de l'état React.
  */
  const [versionId] = useState(() => versionDeLAdresse(window.location.search));
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [carrieres, setCarrieres] = useState<readonly Carriere[]>([]);
  /**
   * Quand la version a été relue parce que l'AUTRE fenêtre a écrit.
   *
   * Affiché, et c'est délibéré. Des chiffres qui bougent tout seuls sous les yeux, sans rien
   * qui dise pourquoi, se lisent comme une erreur de l'écran — on relit trois fois avant de
   * penser à l'autre fenêtre. Une ligne qui dit d'où vient le changement coûte moins que ce
   * doute-là. C'est la même règle que le compteur de ce que l'API n'a pas rendu : un écran qui
   * change sans le dire est un écran qui ment par omission.
   */
  const [venuDAilleurs, setVenuDAilleurs] = useState<string | null>(null);
  const [rechargement, setRechargement] = useState(false);
  /**
   * L'assistant est-il branché sur ce serveur ? Le logo n'apparaît que s'il l'est ET que la
   * version se modifie (docs/assistant-planning.md, D2 et D3) : un logo qui ouvrirait une
   * fenêtre en pause promettrait ce qu'elle ne tient pas.
   */
  const [assistantConfigure, setAssistantConfigure] = useState(false);
  useEffect(() => {
    if (versionId === "") return;
    etatAssistant(versionId)
      .then((e) => {
        setAssistantConfigure(e.configure);
      })
      .catch(() => {
        setAssistantConfigure(false);
      });
  }, [versionId]);

  /**
   * Relire la version entière.
   *
   * `ailleurs` distingue les deux causes : une modification posée ici (la fenêtre sait déjà ce
   * qu'elle a fait) et une modification venue de la contre-étude (il faut le dire). Le drapeau
   * de rechargement remplace l'écran de chargement : vider l'affichage à chaque actualisation
   * ferait clignoter la frise entière pour une barre changée ailleurs.
   */
  const relire = useCallback(
    (ailleurs: boolean): void => {
      if (versionId === "") return;
      setRechargement(true);
      chargerVersion(versionId)
        .then((reponse) => {
          setEtat({ phase: "chargee", reponse });
          if (ailleurs) {
            setVenuDAilleurs(
              new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
            );
          }
        })
        .catch((erreur: unknown) => {
          setEtat({
            phase: "erreur",
            raison: erreur instanceof Error ? erreur.message : String(erreur),
          });
        })
        .finally(() => {
          setRechargement(false);
        });
    },
    [versionId],
  );

  useEffect(() => {
    relire(false);
  }, [relire]);

  useEffect(() => {
    // Les carrières ne changent pas pendant qu'on planifie : lues une fois, comme dans
    // l'application, où elles le sont au retour sur la contre-étude.
    let abandonne = false;
    chargerCarrieres()
      .then(({ carrieres: liste }) => {
        if (!abandonne) setCarrieres(liste);
      })
      // Un référentiel injoignable n'empêche pas de LIRE le planning : seule la pastille de
      // provenance d'un chargement s'en sert.
      .catch(() => undefined);
    return () => {
      abandonne = true;
    };
  }, []);

  /* L'abonnement au canal : ce que la contre-étude écrit arrive ici sans qu'on le demande. */
  useEffect(
    () =>
      ecouterVersion(versionId, () => {
        relire(true);
      }),
    [versionId, relire],
  );

  useEffect(() => {
    if (etat.phase !== "chargee") return;
    // Le titre de la fenêtre porte l'affaire : c'est ce que la barre des tâches montre quand
    // les deux fenêtres sont réduites, et « Planif » deux fois n'y distingue rien.
    document.title = `${etat.reponse.version.apo} — Planning`;
  }, [etat]);

  if (versionId === "") {
    return (
      <main className="fenetre-planning">
        <p className="alerte" role="alert">
          Cette fenêtre s&apos;ouvre depuis une contre-étude : son adresse doit porter la version
          à afficher. Revenir à la contre-étude et cliquer « Ouvrir le planning dans une
          fenêtre ».
        </p>
      </main>
    );
  }

  return (
    <main className="fenetre-planning">
      {etat.phase === "chargement" && <p>Chargement du planning…</p>}
      {etat.phase === "erreur" && <p className="alerte">{etat.raison}</p>}
      {etat.phase === "chargee" && (
        <>
          {/*
            **Ce que le planning ne dit pas déjà de lui-même**, et rien de plus. Mesuré à
            l'écran le 22/09/2026 : la frise porte déjà en tête le n° d'affaire, le nom du
            chantier et son nombre de tâches planifiées, donc les répéter ici affichait deux fois
            l'affaire sur deux lignes consécutives. Ce qui manque, en revanche, c'est **quelle
            version** on regarde — deux fenêtres sur deux versions d'un même chantier sont
            autrement indiscernables.
          */}
          <p className="etat provenance">
            <strong>
              {etat.reponse.version.libelle ?? etat.reponse.version.type}
            </strong>
            {!etat.reponse.modifiable && " · version figée : son contenu ne peut plus changer"}{" "}
            {/*
              **Le bouton reste**, même quand le canal fonctionne. C'est le seul chemin qui ne
              dépende d'aucune capacité du navigateur — et Chris a demandé de « pouvoir
              actualiser », pas d'espérer que ça s'actualise. L'automatique est un confort
              par-dessus, pas la seule porte.
            */}
            <button
              type="button"
              className="lien"
              disabled={rechargement}
              onClick={() => {
                setVenuDAilleurs(null);
                relire(false);
              }}
            >
              {rechargement ? "Actualisation…" : "Actualiser"}
            </button>{" "}
            {/*
              **L'ASSISTANT DE SAISIE** (docs/assistant-planning.md) : ici et nulle part ailleurs —
              dans la fenêtre du planning ouverte depuis la contre-étude, sur une version qui se
              modifie (D2, D3). Sa propre fenêtre, pour un second écran (D11).
            */}
            {etat.reponse.modifiable && assistantConfigure && (
              <button
                type="button"
                className="logo-assistant"
                title="Assistant de saisie : écrire une commande en langage naturel"
                onClick={() => {
                  window.open(
                    adresseDeLAssistant(versionId),
                    nomFenetreAssistant(versionId),
                    "popup=yes,width=560,height=860",
                  );
                }}
              >
                <span aria-hidden="true">✦</span> Assistant
              </button>
            )}
          </p>

          {venuDAilleurs !== null && (
            <p className="etat">
              Mis à jour à {venuDAilleurs} depuis une autre fenêtre (contre-étude ou assistant).
            </p>
          )}

          <Planning
            entete={etat.reponse.version}
            depo={etat.reponse.depo}
            reference={etat.reponse.reference}
            versionId={versionId}
            carrieres={carrieres}
            attelagesDesPostes={etat.reponse.attelages}
            transportsFiges={etat.reponse.transportsFiges}
            modifiable={etat.reponse.modifiable}
            cible={null}
            /* La fenêtre occupe toute la hauteur : c'est elle qui borne la frise, pas une mesure. */
            hauteurGeree
            onChange={() => {
              // Relire ICI, puis prévenir l'autre fenêtre : ce qui se pose sur le planning
              // change le déboursé, et la contre-étude doit le voir sans être rouverte.
              relire(false);
              signalerVersionModifiee(versionId);
            }}
          />
        </>
      )}
    </main>
  );
}
